import React, { useState, useEffect } from 'react';
import {
    Button,
    TextField,
    CircularProgress,
    Typography,
    InputAdornment,
    Icon,
    Dialog,
    DialogContent,
    DialogActions
} from '@material-ui/core';
import { useDispatch } from 'react-redux';
import VerificationCodeDialog from './VerificationCodeDialog';
import { checkTrainingParticipant, loginTraining, verifyTrainingCode } from 'app/store/actions/training.actions';
import { submitLoginForTrainingSession } from 'app/auth/store/actions/auth.actions';
import ClusterStartingNotice from './ClusterStartingNotice';

const TrainingSessionLogin = () => {
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [pausedDialogOpen, setPausedDialogOpen] = useState(false);
    const [infoDialogOpen, setInfoDialogOpen] = useState(false);
    const [infoDialogMessage, setInfoDialogMessage] = useState('');

    // Check URL for pre-filled session code
    const urlParams = new URLSearchParams(window.location.search);
    const codeFromUrl = urlParams.get('code') || '';
    const hasCodeFromUrl = !!codeFromUrl;

    const [formState, setFormState] = useState({
        fullName: '',
        email: '',
        sessionCode: codeFromUrl
    });
    const [step, setStep] = useState(1);
    const [participantExists, setParticipantExists] = useState(false);
    const [existingFullName, setExistingFullName] = useState('');
    const [verificationOpen, setVerificationOpen] = useState(false);
    const [verificationLoading, setVerificationLoading] = useState(false);
    const [verificationError, setVerificationError] = useState('');
    const [domainError, setDomainError] = useState('');
    // Set when the workshop's cluster was asleep and is now starting for this
    // participant. A cloud cluster takes minutes to come back, and without
    // this the student lands on something that is not up yet and concludes
    // the workshop is broken.
    const [clusterStartup, setClusterStartup] = useState(null);
    const [allowedDomains, setAllowedDomains] = useState([]);
    const [licenseError, setLicenseError] = useState('');

    // Clean up states on component mount to prevent stale state issues
    useEffect(() => {
        setError('');
        setDomainError('');
        setLicenseError('');
        setAllowedDomains([]);
        setVerificationError('');
        setVerificationOpen(false);
    }, []);

    const handleChange = (e) => {
        // Clear all error states when user starts typing
        setError('');
        setDomainError('');
        setLicenseError('');
        setAllowedDomains([]);

        setFormState({
            ...formState,
            [e.target.name]: e.target.value
        });
    };

    const getFullName = () => {
        return participantExists ? existingFullName : formState.fullName;
    };

    const clearErrors = () => {
        setError('');
        setDomainError('');
        setAllowedDomains([]);
        setLicenseError('');
    };

    const isWorkshopPausedError = (msg) => msg && (msg.toLowerCase().includes('paused') || msg.toLowerCase().includes('currently paused'));
    const isNoActiveClustersError = (msg) => msg && (msg.toLowerCase().includes('no active cluster') || msg.toLowerCase().includes('no spots available') || msg.toLowerCase().includes('not found'));

    const handleLoginError = (err) => {
        console.error("Login error:", err);

        if (!err) {
            setError('An unexpected error occurred');
            return;
        }

        // Extract error message from various shapes
        var errMsg = '';
        if (err.responseData && err.responseData.error) {
            errMsg = err.responseData.error;
        } else if (err.response && err.response.data) {
            errMsg = err.response.data.message || err.response.data.error || '';
        } else if (err.message) {
            errMsg = err.message;
        }

        // Show paused dialog
        if (isWorkshopPausedError(errMsg)) {
            setPausedDialogOpen(true);
            return;
        }

        // Show info dialog for non-active clusters / session not found
        if (isNoActiveClustersError(errMsg)) {
            setInfoDialogMessage(errMsg);
            setInfoDialogOpen(true);
            return;
        }

        if (err.licenseError) {
            setLicenseError(err.message || 'License capacity reached. Cannot create new session.');
        } else if (err.allowedDomains && Array.isArray(err.allowedDomains)) {
            setDomainError(err.message || 'Email domain not allowed');
            setAllowedDomains(err.allowedDomains);
        } else if (err.responseData && err.responseData.error) {
            setError(err.responseData.error);
        } else if (err.response && err.response.data) {
            const responseData = err.response.data;
            if (responseData.reason === 'LicenseCapacityReached') {
                setLicenseError(responseData.message || 'License capacity reached. Cannot create new session.');
            } else if (responseData.message && responseData.message.includes('capacity')) {
                setLicenseError(responseData.message);
            } else {
                setError(responseData.message || responseData.error || 'Server error occurred');
            }
        } else {
            setError((err && err.message) || 'An unexpected error occurred');
        }
    };

    const proceedWithLogin = async (fullName) => {
        try {
            const result = await dispatch(loginTraining({
                fullName,
                email: formState.email,
                sessionCode: formState.sessionCode
            }));
            if (result && result.verificationRequired) {
                setLoading(false);
                setVerificationOpen(true);
            } else {
                const login = await dispatch(submitLoginForTrainingSession(fullName, formState.sessionCode, formState.email));
                if (!login || !login.ok) {
                    // Sign-in did not take. The error is already on the form;
                    // release the button so the participant can retry.
                    setLoading(false);
                    return;
                }
                if (login.clusterStartup && login.clusterStartup.ready === false) {
                    setClusterStartup(login.clusterStartup);
                }
            }
        } catch (err) {
            handleLoginError(err);
            setLoading(false);
        }
    };

    // Step 1: Check if participant exists
    const handleContinue = async (e) => {
        e.preventDefault();
        setLoading(true);
        clearErrors();

        try {
            const result = await dispatch(checkTrainingParticipant({
                email: formState.email,
                sessionCode: formState.sessionCode
            }));

            if (result.participantExists) {
                // Returning user — skip to login directly
                setParticipantExists(true);
                setExistingFullName(result.fullName);
                await proceedWithLogin(result.fullName);
            } else {
                // New user — show full name field
                setParticipantExists(false);
                setExistingFullName('');
                setStep(2);
                setLoading(false);
            }
        } catch (err) {
            handleLoginError(err);
            setLoading(false);
        }
    };

    // Step 2: New user submits with full name
    const handleJoinWorkshop = async (e) => {
        e.preventDefault();
        setLoading(true);
        clearErrors();
        await proceedWithLogin(formState.fullName);
    };

    const handleBack = () => {
        setStep(1);
        clearErrors();
    };

    const handleVerifyCode = async (code) => {
        setVerificationLoading(true);
        setVerificationError('');
        try {
            const result = await dispatch(verifyTrainingCode({
                email: formState.email,
                sessionCode: formState.sessionCode,
                code
            }));
            if (result.success) {
                const login = await dispatch(submitLoginForTrainingSession(
                    getFullName(),
                    formState.sessionCode,
                    formState.email,
                    result.data && result.data.verificationToken
                ));
                if (!login || !login.ok) {
                    setVerificationError('Could not join the workshop session. Please try again.');
                    return;
                }
                if (login.clusterStartup && login.clusterStartup.ready === false) {
                    setVerificationOpen(false);
                    setClusterStartup(login.clusterStartup);
                }
            } else {
                setVerificationError(result.error);
            }
        } catch (err) {
            setVerificationError('An unexpected error occurred');
        } finally {
            setVerificationLoading(false);
        }
    };

    const handleResendCode = async () => {
        setVerificationError('');
        setLoading(true);
        try {
            await dispatch(loginTraining({
                fullName: getFullName(),
                email: formState.email,
                sessionCode: formState.sessionCode
            }));
            setLoading(false);
        } catch (err) {
            if (!err) {
                setError('An unexpected error occurred');
                setLoading(false);
                return;
            }
            setError((err && err.message) || 'Failed to resend code.');
            setLoading(false);
        }
    };

    // The workshop's cluster is on its way up. Everything else on this screen
    // is done - the participant is signed in - so the only useful thing left
    // is an honest wait.
    if (clusterStartup) {
        return <ClusterStartingNotice startup={clusterStartup} />;
    }

    return (
        <div className="w-full">
            <Typography variant="h6" className="text-center md:w-full mb-48">
                Sign in to workshop session
            </Typography>

            {step === 1 && (
                <form className="flex flex-col justify-center w-full" onSubmit={handleContinue}>
                    <TextField
                        className="mb-16"
                        label="Email"
                        name="email"
                        type="email"
                        value={formState.email}
                        onChange={handleChange}
                        variant="outlined"
                        required
                        fullWidth
                        disabled={loading}
                        error={!!domainError}
                        helperText={domainError ? `Email domain not allowed. Please use an email from one of these domains: ${allowedDomains.join(', ')}` : ''}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <Icon className="text-20" color="action">
                                        email
                                    </Icon>
                                </InputAdornment>
                            )
                        }}
                    />
                    <TextField
                        className="mb-16"
                        label="Workshop Session Code"
                        name="sessionCode"
                        value={formState.sessionCode}
                        onChange={handleChange}
                        variant="outlined"
                        required
                        fullWidth
                        disabled={loading || hasCodeFromUrl}
                        helperText={hasCodeFromUrl ? 'Session code provided via link' : ''}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <Icon className="text-20" color="action">
                                        school
                                    </Icon>
                                </InputAdornment>
                            )
                        }}
                    />
                    {error && (
                        <div className="text-red-500 mt-8 text-center">{error}</div>
                    )}
                    {licenseError && (
                        <div className="text-red-500 mt-8 text-center">{licenseError}</div>
                    )}
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        className="w-full mx-auto mt-16"
                        disabled={!formState.email || !formState.sessionCode || loading}
                        style={{ minHeight: 48, minWidth: 280 }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', minWidth: 240 }}>
                            {loading ? <CircularProgress size={24} /> : 'Continue'}
                        </div>
                    </Button>
                </form>
            )}

            {step === 2 && (
                <form className="flex flex-col justify-center w-full" onSubmit={handleJoinWorkshop}>
                    <Typography variant="body2" className="mb-16 text-center" color="textSecondary">
                        Joining as {formState.email}
                    </Typography>
                    <TextField
                        className="mb-16"
                        label="Full Name"
                        name="fullName"
                        value={formState.fullName}
                        onChange={handleChange}
                        variant="outlined"
                        required
                        fullWidth
                        autoFocus
                        disabled={loading}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <Icon className="text-20" color="action">
                                        person
                                    </Icon>
                                </InputAdornment>
                            )
                        }}
                    />
                    {error && (
                        <div className="text-red-500 mt-8 text-center">{error}</div>
                    )}
                    {licenseError && (
                        <div className="text-red-500 mt-8 text-center">{licenseError}</div>
                    )}
                    <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
                        <Button
                            variant="outlined"
                            onClick={handleBack}
                            disabled={loading}
                            style={{ minHeight: 48, flex: 1 }}
                        >
                            Back
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            disabled={!formState.fullName || loading}
                            style={{ minHeight: 48, flex: 2 }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                                {loading ? <CircularProgress size={24} /> : 'Join Workshop'}
                            </div>
                        </Button>
                    </div>
                </form>
            )}

            <VerificationCodeDialog
                open={verificationOpen}
                onClose={() => {
                    if (!verificationLoading) {
                        setVerificationOpen(false);
                        setVerificationError('');
                    }
                }}
                onVerify={handleVerifyCode}
                onResend={handleResendCode}
                email={formState.email}
                loading={verificationLoading}
                error={verificationError}
            />

            {/* Workshop Paused Dialog */}
            <Dialog
                open={pausedDialogOpen}
                onClose={() => setPausedDialogOpen(false)}
                maxWidth="xs"
                fullWidth
                PaperProps={{ style: { borderRadius: 12, overflow: 'hidden' } }}
            >
                <DialogContent style={{ textAlign: 'center', padding: '32px 24px 16px' }}>
                    <img
                        src="./assets/images/backgrounds/pause.png"
                        alt="Workshop Paused"
                        style={{ width: 280, height: 280, objectFit: 'contain', margin: '0 auto 8px', display: 'block' }}
                    />
                    <Typography variant="h6" style={{ fontWeight: 600, color: '#0f2147', marginBottom: 8 }}>
                        Workshop Paused
                    </Typography>
                    <Typography variant="body2" style={{ color: '#6c757d', lineHeight: 1.6 }}>
                        This workshop session has been temporarily paused by your instructor. Please try again later.
                    </Typography>
                </DialogContent>
                <DialogActions style={{ justifyContent: 'center', padding: '8px 24px 24px' }}>
                    <Button
                        onClick={() => setPausedDialogOpen(false)}
                        variant="contained"
                        style={{
                            textTransform: 'none',
                            backgroundColor: '#1B76D3',
                            color: '#fff',
                            fontWeight: 500,
                            borderRadius: 8,
                            padding: '8px 32px',
                            boxShadow: 'none',
                        }}
                    >
                        OK
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Info Dialog (no active clusters, session not found, etc.) */}
            <Dialog
                open={infoDialogOpen}
                onClose={() => setInfoDialogOpen(false)}
                maxWidth="xs"
                fullWidth
                PaperProps={{ style: { borderRadius: 12 } }}
            >
                <DialogContent style={{ textAlign: 'center', padding: '32px 24px 16px' }}>
                    <div style={{ width: '100%', height: 200, overflow: 'hidden', margin: '-32px auto 8px' }}>
                        <img
                            src="./assets/images/backgrounds/cable.png"
                            alt="Session Unavailable"
                            style={{ width: '160%', maxWidth: 'none', objectFit: 'cover', objectPosition: 'left center', display: 'block', marginLeft: '-55%', marginTop: '-80px', opacity: 0.8 }}
                        />
                    </div>
                    <Typography variant="h6" style={{ fontWeight: 600, color: '#0f2147', marginBottom: 8 }}>
                        Session Unavailable
                    </Typography>
                    <Typography variant="body2" style={{ color: '#6c757d', lineHeight: 1.6 }}>
                        {infoDialogMessage}
                    </Typography>
                </DialogContent>
                <DialogActions style={{ justifyContent: 'center', padding: '8px 24px 24px' }}>
                    <Button
                        onClick={() => setInfoDialogOpen(false)}
                        variant="contained"
                        style={{
                            textTransform: 'none',
                            backgroundColor: '#1B76D3',
                            color: '#fff',
                            fontWeight: 500,
                            borderRadius: 8,
                            padding: '8px 32px',
                            boxShadow: 'none',
                        }}
                    >
                        OK
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default TrainingSessionLogin;
